// <shipping-protection>
//
// Owns adding/removing the placeholder shipping-protection line in the cart
// (the Cart Transform function can only reprice existing lines, not add or
// remove them). The cart attribute `_shipping_protection` records the
// customer's preference and is written together with the product in a single
// Ajax cart request.
//
// Protection only applies to an "insurable" cart — one holding at least one
// product the policy covers (not the placeholder line, not an excluded product
// type like gift cards / cores / tune files). Whether the cart is insurable is
// computed server-side in snippets/embark-shipping-protection.liquid and handed
// in via data attributes, so this component never inspects cart rows:
//
//  - data-checked   : the preference (cart attribute), drives the visual state.
//  - data-in-cart   : is the placeholder line currently in the cart.
//  - data-insurable : does the cart hold anything protection can cover.
//
// Toggling always records the preference so it sticks, but only adds the
// placeholder line when the cart is insurable. maybeAutoEnable() re-adds the
// line once an insurable product is present; maybeAutoDisable() removes it when
// the cart is no longer insurable (e.g. only gift cards remain), keeping the
// preference so protection returns when an insurable product is added again.

if (!customElements.get('shipping-protection')) {
  const ATTR_KEY = '_shipping_protection';
  const EXCLUDED_ATTR_KEY = '_shipping_protection_excluded_amount';
  const STORAGE_KEY = 'Include Shipping Protection';
  const SELECTOR_BUTTON = '.shipping-check';

  let autoEnableInFlight = false;
  let autoDisableInFlight = false;
  let excludedSyncInFlight = false;
  // Last excluded-cents value written to the cart attribute. Null until the
  // first write. Guards maybeSyncExcluded() against re-writing an unchanged
  // value (and against a post-write refresh re-triggering it and looping).
  let lastSyncedExcluded = null;

  class ShippingProtection extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector(SELECTOR_BUTTON);
      if (this.button) {
        this.button.addEventListener('click', this.onToggleClick.bind(this));
      }

      this.maybeAutoEnable();
      this.maybeAutoDisable();
      this.maybeSyncExcluded();
    }

    get variantId() {
      return this.dataset.variantId;
    }

    // Customer preference (mirrored to localStorage), not whether the
    // placeholder line is currently in the cart.
    get isChecked() {
      return this.dataset.checked === 'true';
    }

    get inCart() {
      return this.dataset.inCart === 'true';
    }

    get insurable() {
      return this.dataset.insurable === 'true';
    }

    // Reflects the checked state on the toggle button. Used for the optimistic
    // flip on click; the next server render is the source of truth.
    setChecked(checked) {
      if (!this.button) return;
      this.button.classList.toggle('checked', checked);
      this.button.classList.toggle('is-checked', checked);
      this.button.setAttribute('aria-pressed', checked ? 'true' : 'false');
    }

    async onToggleClick(event) {
      event.preventDefault();
      if (this.busy) return;

      this.busy = true;
      this.setAttribute('aria-busy', 'true');

      this.setChecked(!this.isChecked);

      ShippingProtection.dispatchCartChange('start');

      try {
        if (this.isChecked) {
          await this.toggleOff();
        } else {
          await this.toggleOn();
        }
        await ShippingProtection.refreshCart();
      } catch (err) {
        console.error('[shipping-protection]', err); // eslint-disable-line
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        ShippingProtection.dispatchCartChange('end');
      }
    }

    toggleOn() {
      ShippingProtection.setStoredPreference(true);
      // Record the preference either way, but only add the placeholder line when
      // there's something to insure. An excluded-only cart has no line to
      // charge, so just flip the attribute and let maybeAutoEnable() add the
      // line once an insurable product is present.
      if (this.insurable) {
        return ShippingProtection.addProtection(Number(this.variantId), {
          [EXCLUDED_ATTR_KEY]: this.excludedCentsTarget
        });
      }
      return ShippingProtection.ajaxCart('/cart/update.js', {
        attributes: { [ATTR_KEY]: 'true' }
      });
    }

    toggleOff() {
      ShippingProtection.setStoredPreference(false);
      // Setting a not-present variant to 0 is a harmless no-op, so this single
      // call covers both the "line in cart" and "preference only" states. Clear
      // the excluded amount alongside the preference — with no protection line
      // there's nothing for the function to reprice, so it shouldn't linger.
      lastSyncedExcluded = '';
      return ShippingProtection.ajaxCart('/cart/update.js', {
        updates: { [this.variantId]: 0 },
        attributes: { [ATTR_KEY]: '', [EXCLUDED_ATTR_KEY]: '' }
      });
    }

    async maybeAutoEnable() {
      if (this.inCart) return;
      if (autoEnableInFlight) return;
      if (!this.insurable) return;
      if (!ShippingProtection.getStoredPreference()) return;

      autoEnableInFlight = true;
      ShippingProtection.dispatchCartChange('start');
      try {
        await ShippingProtection.addProtection(Number(this.variantId), {
          [EXCLUDED_ATTR_KEY]: this.excludedCentsTarget
        });
        await ShippingProtection.refreshCart();
      } catch (err) {
        console.error('[shipping-protection] auto-enable failed', err); // eslint-disable-line
      } finally {
        autoEnableInFlight = false;
        ShippingProtection.dispatchCartChange('end');
      }
    }

    async maybeAutoDisable() {
      // Never leave the placeholder line in a cart that's no longer insurable
      // (only the placeholder and/or excluded types like gift cards remain).
      // Keep the stored preference and attribute so protection returns when an
      // insurable product is added again.
      if (!this.inCart) return;
      if (this.insurable) return;
      if (autoEnableInFlight || autoDisableInFlight) return;

      autoDisableInFlight = true;
      ShippingProtection.dispatchCartChange('start');
      try {
        await ShippingProtection.ajaxCart('/cart/update.js', {
          updates: { [this.variantId]: 0 }
        });
        await ShippingProtection.refreshCart();
      } catch (err) {
        console.error('[shipping-protection] auto-disable failed', err); // eslint-disable-line
      } finally {
        autoDisableInFlight = false;
        ShippingProtection.dispatchCartChange('end');
      }
    }

    // Total cents of excluded-type components inside native fixed bundles,
    // computed server-side (the Cart Transform can't see inside a bundle line,
    // so it reads the _shipping_protection_excluded_amount cart attribute
    // instead). '0' normalizes to '' so the attribute is cleared rather than
    // kept at zero.
    get excludedCentsTarget() {
      const cents = this.dataset.excludedCents;
      return cents && cents !== '0' ? cents : '';
    }

    // Pushes the server-computed excluded-bundle total into the cart attribute
    // the Cart Transform reads. Runs on connect — i.e. on every cart re-render —
    // so it covers the paths the component doesn't drive itself: a bundle added
    // while protection is already in the cart, or a bundle's quantity changed.
    // The amount can't be derived in JS (bundle component types/prices aren't in
    // the Ajax cart), so it's read from data-excluded-cents, which connect
    // guarantees is freshly rendered. Writes at most once per change: only when
    // a protection line exists to reprice, and only when the value drifted from
    // what we last wrote (lastSyncedExcluded) — that guard also stops the
    // post-write refresh's reconnect from re-writing and looping.
    async maybeSyncExcluded() {
      if (!this.inCart) return;
      const amount = this.excludedCentsTarget;
      if (amount === lastSyncedExcluded) return;
      if (excludedSyncInFlight) return;

      excludedSyncInFlight = true;
      ShippingProtection.dispatchCartChange('start');
      try {
        await ShippingProtection.ajaxCart('/cart/update.js', {
          attributes: { [EXCLUDED_ATTR_KEY]: amount }
        });
        lastSyncedExcluded = amount;
        // Refresh so the drawer totals pick up the repriced protection line.
        await ShippingProtection.refreshCart();
      } catch (err) {
        console.error('[shipping-protection] excluded sync failed', err); // eslint-disable-line
      } finally {
        excludedSyncInFlight = false;
        ShippingProtection.dispatchCartChange('end');
      }
    }

    static getStoredPreference() {
      // Defaults to false. Only enabled if the customer has explicitly
      // toggled protection on at some point — and then it persists across
      // checkouts (which clear cart attributes but not localStorage), so
      // their next cart auto-adds protection alongside the first product.
      return localStorage.getItem(STORAGE_KEY) === 'true';
    }

    static setStoredPreference(enabled) {
      localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
    }

    static async refreshCart() {
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        await cartDrawer.refresh();
        return;
      }
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.refresh === 'function') {
        await cartItems.refresh();
      }
    }

    static dispatchCartChange(phase) {
      document.dispatchEvent(new CustomEvent(`on:cart:change:${phase}`, {
        bubbles: true,
        detail: { source: 'shipping-protection' }
      }));
    }

    // Adds the placeholder line, guaranteeing it never ends up above qty 1.
    // /cart/add.js increments an existing line, so a raced or duplicate add can
    // push the placeholder to 2 — but the add response carries that line's
    // resulting quantity, so we clamp it back down from the same call rather
    // than polling /cart.js. (Nothing other than this component ever adds the
    // placeholder variant, so the add response is the only place a dupe arises.)
    static async addProtection(variantId, extraAttributes) {
      const res = await ShippingProtection.ajaxCart('/cart/add.js', {
        items: [{ id: variantId, quantity: 1 }],
        attributes: { [ATTR_KEY]: 'true', ...extraAttributes }
      });
      // The excluded total rode along on this write; record it so the
      // standalone sync doesn't re-write the same value on reconnect.
      if (extraAttributes && EXCLUDED_ATTR_KEY in extraAttributes) {
        lastSyncedExcluded = extraAttributes[EXCLUDED_ATTR_KEY];
      }

      const items = res && res.items ? res.items : (res ? [res] : []);
      const line = items.find((item) => item.variant_id === variantId);
      if (line && line.quantity > 1) {
        await ShippingProtection.ajaxCart('/cart/update.js', { updates: { [variantId]: 1 } });
      }
      return res;
    }

    static async ajaxCart(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`${url} → ${response.status}: ${text}`);
      }

      return response.json();
    }
  }

  customElements.define('shipping-protection', ShippingProtection);
}
